import React, { FC } from 'react'
import { Box, TextField, Select, MenuItem, Typography, Button, Stack, FormControl } from '@mui/material'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import CloseIcon from '@mui/icons-material/Close'
import { DOCUMENT_TYPES } from './formsForm.constants'
import { getAcceptedFileTypes } from './useFormsFormHandlers'

interface FormsFormFieldsProps {
  name: string
  type: string
  document: File | string | null
  editingId: string | null
  isSubmitting: boolean
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onTypeChange: (type: string) => void
  onDocumentSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveDocument: () => void
  onReset: () => void
}

const getDocumentDisplay = (doc: File | string | null) => {
  if (doc instanceof File) {
    return {
      name: doc.name,
      size: (doc.size / 1024).toFixed(2) + ' KB',
      isNew: true,
    }
  }

  if (typeof doc === 'string' && doc.trim().length > 0) {
    const nameMatch = doc.match(/[^/\\&?]+\.\w{3,4}(?:[?#].*)?$/)
    const name = nameMatch ? nameMatch[0] : doc

    return {
      name: name,
      size: 'Existente',
      isNew: false,
    }
  }

  return null
}

const FormsFormFields: FC<FormsFormFieldsProps> = ({
  name,
  type,
  document,
  editingId,
  isSubmitting,
  onNameChange,
  onTypeChange,
  onDocumentSelect,
  onRemoveDocument,
  onReset,
}) => {
  const docDisplay = getDocumentDisplay(document)

  return (
    <Stack spacing={4}>
      {/* Primera Fila: Nombre, Tipo, Upload */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.5fr 1.5fr 1.5fr' },
          gap: 3,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Titulo <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <TextField
            size="medium"
            value={name}
            onChange={onNameChange}
            placeholder="Formato de solicitud"
            fullWidth
            required
            onInvalid={(e) => {
              (e.target as HTMLInputElement).setCustomValidity('Por favor completa este campo')
            }}
            onInput={(e) => {
              (e.target as HTMLInputElement).setCustomValidity('')
            }}
          />
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Tipo <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <FormControl size="medium" fullWidth>
            <Select
              value={type}
              onChange={(e) => onTypeChange(e.target.value)}
              required
              onInvalid={(e) => {
                (e.target as HTMLInputElement).setCustomValidity('Por favor selecciona un tipo de documento')
              }}
              onInput={(e) => {
                (e.target as HTMLInputElement).setCustomValidity('')
              }}
            >
              {DOCUMENT_TYPES.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-end', position: 'relative' }}>
          <Button
            variant="contained"
            component="label"
            endIcon={<FileUploadIcon />}
            disabled={!type}
            sx={{
              gap: 1.5,
              width: '100%',
              backgroundColor: type ? 'primary.500' : '#9ca3af',
              color: '#fff',
              textTransform: 'none',
              py: 2,
              fontSize: '14px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: type ? '#4A7AB5' : '#9ca3af',
                boxShadow: 'none',
              },
            }}
          >
            {docDisplay ? 'Cambiar documento' : 'Subir documento'}
            <input
              hidden
              accept={getAcceptedFileTypes(type)}
              type="file"
              onChange={onDocumentSelect}
              key={type}
            />
          </Button>
          {!type && (
            <Typography
              variant="caption"
              color="error"
              sx={{ position: 'absolute', bottom: -20, fontSize: '11px' }}
            >
              Selecciona primero el tipo
            </Typography>
          )}
        </Box>
      </Box>

      {/* Archivo Seleccionado */}
      {docDisplay && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              backgroundColor: docDisplay.isNew ? '#eff6ff' : '#f0fdf4',
              borderRadius: 1,
              border: docDisplay.isNew ? '1px solid #dbeafe' : '1px solid #bbf7d0',
              maxWidth: 500,
            }}
          >
            <InsertDriveFileIcon
              sx={{
                color: docDisplay.isNew ? 'primary.main' : '#16a34a',
                fontSize: 24,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                }}
              >
                {docDisplay.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '12px' }}>
                {docDisplay.size}
              </Typography>
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onRemoveDocument}
              sx={{
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: 0.5,
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: docDisplay.isNew ? '#dbeafe' : '#dcfce7',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18, color: '#6b7280' }} />
            </Box>
          </Box>
        </Box>
      )}

      {/* Botones */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6, gap: 4 }}>
        {editingId && (
          <Button
            variant="outlined"
            onClick={onReset}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.2,
              fontSize: '14px',
              color: '#6b7280',
              borderColor: '#d1d5db',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f9fafb',
              },
            }}
          >
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{
            backgroundColor: '#1e3a5f',
            textTransform: 'none',
            px: 6,
            py: 1.2,
            fontSize: '14px',
            '&:hover': {
              backgroundColor: '#152a45',
            },
          }}
        >
          {editingId ? 'Actualizar' : 'Agregar'}
        </Button>
      </Box>
    </Stack>
  )
}

export default FormsFormFields
